import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";

const UnsavedNavigationContext = createContext(null);

// Let the router handle history traversal without inserting synthetic entries.
export function UnsavedNavigationProvider({ children }) {
  const [guard, setDirty] = useState({ dirty: false });
  const dirty = guard.dirty;
  const blocker = useBlocker(dirty);
  const dialog = useRef(null);
  const keep = useRef(null);
  const blocked = blocker.state === "blocked";
  useEffect(() => {
    if (!blocked) return undefined;
    const previousFocus = document.activeElement;
    dialog.current?.showModal();
    keep.current?.focus();
    return () => { if (previousFocus?.isConnected) previousFocus.focus(); };
  }, [blocked]);
  // A completed local save makes an already waiting navigation safe.
  useEffect(() => {
    if (blocked && !dirty) blocker.proceed();
  }, [blocked, dirty, blocker]);
  return <UnsavedNavigationContext.Provider value={setDirty}>
    {children}
    {blocked && <dialog ref={dialog} aria-labelledby="unsaved-quotation-title" aria-describedby="unsaved-quotation-description"
      data-testid="unsaved-navigation-dialog" onCancel={(event) => { event.preventDefault(); blocker.reset(); }}
      className="w-[calc(100%_-_2rem)] max-w-md rounded-lg border border-slate-300 bg-white p-5 text-slate-900 shadow-xl backdrop:bg-slate-950/40">
      <h2 id="unsaved-quotation-title" className="text-lg font-semibold">{guard.title || "Keep your quotation changes?"}</h2>
      <p id="unsaved-quotation-description" className="mt-3 text-sm leading-6">{guard.description || "This quotation has unsaved changes. Keep editing to save them, or discard these edits and leave. Previously saved drafts will remain available."}</p>
      <div className="mt-5 flex flex-wrap justify-end gap-3">
        <button ref={keep} type="button" className="phx-button phx-button-primary" onClick={() => blocker.reset()}>Keep editing</button>
        <button type="button" className="phx-button" onClick={() => blocker.proceed()}>{guard.leaveLabel || "Discard changes and leave"}</button>
      </div>
    </dialog>}
  </UnsavedNavigationContext.Provider>;
}

export function useUnsavedNavigation(dirty, { title, description, leaveLabel } = {}) {
  const setDirty = useContext(UnsavedNavigationContext);
  useLayoutEffect(() => {
    if (!setDirty) return undefined;
    setDirty({ dirty: Boolean(dirty), title, description, leaveLabel });
    return () => setDirty({ dirty: false });
  }, [dirty, setDirty, title, description, leaveLabel]);
  return Boolean(setDirty);
}
